import { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';

import OutgoingText from './OutgoingText';
import IncomingText from './IncomingText';
import UploadDocuments from '../../modals/UploadDocuments';
import { useAppDispatch } from '../../../store/hooks';
import { NUMBERS } from '../../../resources/number-constants';
import { aiChat, getChats } from '../../../store/features/dashboard';
import { chatPlaneIcon, climateLogo, proceedArrow, paperclipIcon } from '../../../assets/images/images';
import { removeNullValues } from '../../../utility/helpers';
import { ChatMessage } from '../../../utility/chatUtils';

const CreateWithAI = ({
    convoId,
    setConvoId,
    userDetails,
    conversationId,
    generateJobDescription,
    handleAutoSave
}: any) => {
    const dispatch = useAppDispatch();
    const messageRef = createRef<HTMLTextAreaElement>();
    const [isLoading, setIsLoading] = useState(false);
    const [chatList, setChatList] = useState<ChatMessage[]>([]);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const [lastMessageId, setLastMessageId] = useState<number | null>(null);
    const [lastQuestionStep, setLastQuestionStep] = useState<string | null>(null);
    const [showSendButton, setShowSendButton] = useState(false);
    const [inputMessage, setInputMessage] = useState<string | null>(null);
    const [enableGenerateResponse, setEnableGenerateResponse] = useState(false);
    const [showUploadDocModal, setShowUploadDocModal] = useState(false);

    // Scroll to the bottom of chat
    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    // Memoize requestPayload to avoid recreation on every render
    const requestPayload = useCallback((options: string | string[] | object) => {
        const body: any = {
            step: lastQuestionStep,
            payload: {
                title: inputMessage ? null : options,
                message: inputMessage ? options : null,
            },
            is_multi_select: Array.isArray(options) && options?.length > 1,
            conversation_id: conversationId ? conversationId : convoId,
            payload_type: NUMBERS.THREE,
            user: userDetails?.user?.id,
        };
        
        // Only include parent_id if it's a valid backend ID (not a temporary local ID)
        const isValidParentId = !!lastMessageId && 
                                typeof lastMessageId === 'number' && 
                                Array.isArray(chatList) && 
                                chatList.some((m: any) => m?.id === lastMessageId && !m?.is_local);
        if (isValidParentId) {
            body.parent_id = lastMessageId;
        }
        
        return body;
    }, [lastMessageId, lastQuestionStep, conversationId, userDetails, inputMessage, convoId, chatList]);

    // Fetch chat details with loader handling
    const fetchChatDetails = useCallback(async () => {
        try {
            setIsLoading(true); // Show loader
            const payload = { conversation_id: convoId, limit: 300 };
            const res = await dispatch(getChats(payload)).unwrap();
            setChatList(res?.data);
        } catch (error) {
            console.info('Error during fetching chat details:', error);
        } finally {
            setIsLoading(false); // Hide loader
        }
    }, [dispatch, convoId]);

    const handleSendMessage = async (options: string | string[] | object, initialAnswer: string | null) => {
        if (messageRef.current) {
            messageRef.current.value = '';
            messageRef.current.style.height = '56px';
        }
        const payload = requestPayload(options);

        const newMessage = {
            id: `temp_${Date.now()}`,
            payload: { ...payload.payload },
            is_multi_select: payload.is_multi_select,
            parent_id: payload.parent_id,
            conversation_id: payload.conversation_id,
            payload_type: payload.payload_type,
            user: payload.user,
        };

        setChatList((prevChatList: ChatMessage[]) => [...prevChatList, newMessage]);
        setShowSendButton(false);
        try {
            setIsLoading(true);
            const response = await dispatch(aiChat(removeNullValues(payload))).unwrap();
            setInputMessage(null);
            if (!conversationId && !convoId) {
                setConvoId(response?.data?.conversation_id?.id);
            }
            
            // Add the incoming message from the API response directly
            if (response?.data) {
                setChatList((prevChatList: ChatMessage[]) => [...prevChatList, response.data]);
            }
            setIsLoading(false);
            handleAutoSave();
        } catch (error) {
            console.info('Error sending message:', error);
            setIsLoading(false);
            // Optionally handle reverting optimistic update on error
        }
    };

    const send = (event: React.FormEvent) => {
        event.preventDefault();

        const messageInput = messageRef.current;

        if (messageInput) {
            const trimmedMessage = messageInput.value.trim();

            if (trimmedMessage) {
                setShowSendButton(true);
                handleSendMessage(trimmedMessage, null);
                messageInput.value = '';
                messageInput.style.height = '56px';
            }
        }
    };

    // Input change handler for trimming spaces and showing send button
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const trimmedInput = e.target.value.trim(); // Trim input in real-time

        // Update the state only if the message contains any non-space characters
        setInputMessage(trimmedInput.length > 0 ? e.target.value : null);
        if (trimmedInput.length > 0 ) {
            setShowSendButton(true);
        } else {
            setShowSendButton(false);
        }
    };

    const handleEnterKeyPress = (event: React.KeyboardEvent) => {
        if ((event.key === 'Enter') && !event.shiftKey) {
            event.preventDefault();
            send(event);
        } else if ((event.key === 'Enter') && event.shiftKey) {
            event.stopPropagation();
        }
    };

    const adjustTextareaSizeAndNotifyTyping = (event: React.FormEvent<HTMLTextAreaElement>) => {
        const target = event.target as HTMLTextAreaElement;
        const { value, scrollHeight } = target;
        const isEmpty = value === '' || value === '\n';
    
        if (isEmpty) {
            target.style.height = '56px';
        } else {
            target.style.height = `${scrollHeight}px`;
        }
    };

    const renderTooltip = (props: any) => (
        <Tooltip id="button-tooltip" {...props}>
          {enableGenerateResponse ? 'You can now able to generate job' : 'Please fulfill the details for required job description result'}
        </Tooltip>
    );

    const renderAttachmentTooltip = (props: any) => (
        <Tooltip id="attachment-tooltip" {...props}>
          Upload job description, call transcript, or URL link
        </Tooltip>
    );

    const handleGenerate = () => {
        if (!enableGenerateResponse) {
            return;
        }
        generateJobDescription();
    };

    // Scroll to bottom when chat list is updated or loader changes
    useEffect(() => {
        scrollToBottom();
    }, [chatList, isLoading]);

    useEffect(() => {
        if (convoId) {
            fetchChatDetails();
        }
    }, [convoId, fetchChatDetails]);

    useEffect(() => {
        if (conversationId) {
            setConvoId(conversationId);
        }
    }, [conversationId, setConvoId]);
    return (
        <>
            <div className="container">
                <div className="client-chat-main chat-with-ai">
                    <div className="messaging">
                        <div className="inbox_msg">
                            <div className="mesgs" ref={chatContainerRef}>
                                <div className="msg_history">
                                    <div className="chat-ai-profile-wrap">
                                        <img src={climateLogo} alt="profile" className="chat-ai-profile-img" />
                                        <h1 className="chat-profile-head">
                                            Introducing Hunter
                                        </h1>
                                        <span className="chat-profile-des righteous-head">your personal AI Recruiting Assistant<span className="green-dot">.</span></span>
                                    </div>

                                    {chatList?.length > 0 && chatList.map((chat: ChatMessage) => (
                                        <div key={chat?.id || chat?.timestamp} className='incoming-outgoing-wrap'>
                                            {(chat?.payload_type === NUMBERS.THREE) && (
                                                <OutgoingText
                                                    chat={chat}
                                                    companyLogo={userDetails?.logo}
                                                />
                                            )}
                                            {(chat?.payload_type === NUMBERS.FOUR) && (
                                                <IncomingText
                                                    chat={chat}
                                                    isLoading={isLoading}
                                                    setEnableGenerateResponse={setEnableGenerateResponse}
                                                    message={chat?.result}
                                                    setLastMessageId={setLastMessageId}
                                                    handleSendMessage={handleSendMessage}
                                                    setLastQuestionStep={setLastQuestionStep}
                                                />
                                            )}
                                        </div>
                                    ))}

                                    {isLoading && (
                                        <div className="wrapper bounce-loader-wrapper">
                                            <div className="circle"></div>
                                            <div className="circle"></div>
                                            <div className="circle"></div>
                                        </div>
                                    )}
                                </div>

                                <div className="textarea-chat-row">
                                    <div className="textarea-wrap">
                                        <OverlayTrigger
                                            placement="top"
                                            delay={{ show: 250, hide: 400 }}
                                            overlay={renderAttachmentTooltip}
                                        >
                                            <img
                                                src={paperclipIcon}
                                                alt="attachment"
                                                className="chat-attachment-btn"
                                                onClick={() => setShowUploadDocModal(true)}
                                            />
                                        </OverlayTrigger>
                                        <textarea
                                            className="form-control"
                                            id="exampleFormControlTextarea1"
                                            rows={3}
                                            onInput={adjustTextareaSizeAndNotifyTyping}
                                            placeholder="Type here to ask anything"
                                            onChange={handleInputChange}
                                            ref={messageRef}
                                            onKeyDown={(e) => handleEnterKeyPress(e)}
                                        >
                                        </textarea>
                                        <img
                                            src={chatPlaneIcon}
                                            alt="send"
                                            className={`chat-send-btn ${showSendButton ? '' : 'd-none'}`} // Show/hide button based on input
                                            onClick={() => inputMessage && handleSendMessage(inputMessage, null)} // Send message on click
                                        />
                                    </div>
                                    <OverlayTrigger
                                        placement="top"
                                        delay={{ show: 250, hide: 400 }}
                                        overlay={renderTooltip}
                                        >
                                            <Button
                                                className={`generate-btn ${!enableGenerateResponse ? 'disable' : ''}`}
                                                onClick={handleGenerate}
                                            >
                                                Generate
                                                <span className='btn-right-img'>
                                                    <img src={proceedArrow} alt="generate" />
                                                </span>
                                            </Button>
                                    </OverlayTrigger>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showUploadDocModal && (
                <UploadDocuments
                    userId={userDetails?.userId}
                    showUploadDocModal={showUploadDocModal}
                    setShowUploadDocModal={setShowUploadDocModal}
                />
            )}
        </>
    );
};

export default CreateWithAI;
